import { stringify } from 'query-string'
import { GetListParams } from 'react-admin'
import CFG from 'services/cfg'
import {
  FilterFunction,
  Memoizer,
  ProductName,
  ProductServiceName,
  WithCode,
  WithId,
  WithIdAndName,
} from 'types/common'
import { ActivityStatus } from 'types/form'
import { TimeLabel } from './date'

/**
 * lodash get clone => https://lodash.com/docs/#get
 * @param {Object} obj
 * @param {String} path
 * @param {any} def default
 */
export const _get = function (obj: any, path: string, def?: any) {
  /**
   * If the path is a string, convert it to an array
   * @param  {String|Array} path The path
   * @return {Array}             The path array
   */
  const stringToPath = function (path: string) {
    // If the path isn't a string, return it
    if (typeof path !== 'string') return path

    // Create new array
    const output: string[] = []

    // Split to an array with dot notation
    path.split('.').forEach(function (item, index) {
      // Split to an array with bracket notation
      item.split(/\[([^}]+)\]/g).forEach(function (key) {
        // Push to the new array
        if (key.length > 0) {
          output.push(key)
        }
      })
    })

    return output
  }

  // Get the path as an array
  const slicedPath = stringToPath(path)

  // Cache the current object
  let current = obj

  // For each item in the path, dig into the object
  for (let i = 0; i < slicedPath.length; i++) {
    // If the item isn't found, return the default (or null)
    if (!current[slicedPath[i]]) return def

    // Otherwise, update the current  value
    current = current[slicedPath[i]]
  }

  return current
}

export const getDefaultListParams = (): GetListParams => ({
  filter: '',
  pagination: {
    page: 1,
    perPage: CFG.perPageDefault,
  },
  sort: {
    field: '',
    order: '',
  },
})

/**
 * this fn is performing a deep clone of an object containing values (no functions) without checking at circular deps
 * it needs casting. Maybe could be enhanced.
 * @param item unknown
 */
export const cloneDeep = (item: unknown): unknown => {
  if (item === null) {
    return null
  } else if (typeof item !== 'object') {
    return item
  } else if (Array.isArray(item)) {
    return item.map(cloneDeep)
  } else {
    return Object.entries(item as { [key: string]: unknown }).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: cloneDeep(value) }),
      {}
    )
  }
  // console.error(`cloneDeep issue: trying to clone an element of ${typeof item} type`)
}

export type ValueOf<T> = T[keyof T]

type Entries<T> = {
  [K in keyof T]: [K, T[K]]
}[keyof T][]

export type PossiblyEmptyString<T, K extends keyof T> = {
  [W in K]: T[W] | ''
} & {
  [J in Exclude<keyof T, K>]: T[J]
}

export const convertEmptyStringToUndefined = <T extends unknown>(
  p: T | undefined | ''
): T | undefined => (p === '' ? undefined : p)

export const entries = <T extends {}>(obj: T): Entries<T> =>
  Object.entries(obj) as any

export const keys = <T extends {}>(obj: T) => Object.keys(obj) as (keyof T)[]

export const getId = <T extends WithId>(item: T) => item.id

export const getName = <T extends { name: string }>(item: T) => item.name

export const getStringId = <T extends { id: string }>(item: T) => item.id

export const getWithCode = <T extends WithCode>(item: T) => ({
  code: item.code,
})

export const getById = <T extends WithId>(collection: T[], id?: number) =>
  collection.find((item) => item.id === id)

export const getSelectInputChoice = (item: WithIdAndName) => ({
  id: item.id,
  name: item.name,
})

export const roundValue = (value: number) => {
  if (!value) return value

  return value.toFixed(2)
}

export const noDecimals = (n: number) => Math.floor(n)

export type MapFunction<Source, TargetVal> = (
  value: ValueOf<Source>,
  key: keyof Source,
  obj: Source
) => TargetVal

/**
 * Creates an object with the same keys as object and values generated by running each own enumerable
 * string keyed property of object thru iteratee. The iteratee is invoked with three arguments: (value, key, object).
 * @param {Function} mapFn
 * @param {Object} obj
 */
export const mapValues = <Source extends {}, TargetVal>(
  mapFn: MapFunction<Source, TargetVal>,
  obj: Source
) =>
  (Object.keys(obj) as (keyof Source)[]).reduce(
    (acc, key) => ({ ...acc, [key]: mapFn(obj[key], key, obj) }),
    {}
  ) as Record<keyof Source, TargetVal>
/**
 * util to filter objects
 * @param keepFn filter function
 * that accept as first param value and second key of object
 * @param obj object we want filter
 * @returns object with keys filtered
 */
export const filterObject = <Source extends {}>(
  /**
   * @param val value of Object
   * @param key
   * @returns boolean
   */
  keepFn: (val: ValueOf<Source>, key?: keyof Source, obj?: Source) => boolean,
  obj: Source
) =>
  keys(obj).reduce(
    (acc, key) => ({
      ...acc,
      ...(keepFn(obj[key], key, obj) ? { [key]: obj[key] } : {}),
    }),
    {}
  ) as Partial<Source>

export const getPositiveOrZero = (value: number) => Math.max(value, 0)

export const getAggregateValue = <T>(
  collection: T[],
  getAddend: (arg: T) => number
) => collection.map(getAddend).reduce((acc, item) => acc + item ?? 0, 0)

export const isDefined = <T>(itm: T | undefined): itm is T => !!itm

export const objArrayIntersection = (...arrays: WithId[][]) => {
  const [first, ...others] = arrays
  return first.reduce(
    (acc, itm) => [
      ...acc,
      ...(others.every((arr) => arr.map(getId).includes(itm.id)) ? [itm] : []),
    ],
    [] as WithId[]
  )
}

/**
 * @param {String} search location search (i.e: '?filter={id:33}')
 * @returns {Object} the object parsed from the filter QS
 */
export const getObjFromSearch = (search: string) => {
  const params = new URLSearchParams(search) // https://caniuse.com/#search=URLSearchParams
  const obj = params.get('filter')
  if (obj) {
    return JSON.parse(obj)
  }
}

export const getQs = () => {
  const qs = window.location.href.match(/\?.*/)
  if (!qs) {
    console.error('Query string not found')
  } else {
    return qs[0]
  }
}

export const redirectToProductSection = (basePath: string, id?: number) => () =>
  `/${basePath}?${stringify({
    filter: JSON.stringify({ id }),
  })}`

export const nonMemoizer: Memoizer = (fn, _deps) => fn()

/**
 * NON-efficient way to compare 2 objects.
 * Use with caution & at your own risk.
 *
 * @param obj1
 * @param obj2
 * @returns true if obj are equals
 */
export const equals = (obj1: any, obj2: any) =>
  JSON.stringify(obj1) === JSON.stringify(obj2)

export const getServiceDiscriminator = (productName: ProductName) =>
  `${productName.charAt(0).toUpperCase() + productName.slice(1)}Configurator`

export const getDiscriminatorFromService = (
  productService:
    | Exclude<ProductServiceName, 'locationService'>
    | 'generalService'
) =>
  getServiceDiscriminator(productService.replace('Service', '') as ProductName)

export const isEmptyObject = (obj: object) => Object.values(obj).length === 0

export const filterByAllCriteria =
  <T>(filterFunctionArray: FilterFunction<T>[]) =>
  (element: T) =>
    filterFunctionArray.map((fn) => fn(element)).every(isDefined)

export const getIndexById = <T extends WithId>(
  collection: T[],
  id?: number
) => {
  const index = collection.findIndex((item) => item.id === id)
  return index !== -1 ? index : undefined
}

export const distinct = <TItem>(
  collection: TItem[],
  selector: (item: TItem) => unknown
) => {
  const dict: unknown[] = []
  return collection.filter((i) => {
    const selected = selector(i)
    if (!dict.includes(selected)) {
      dict.push(selected)
      return true
    }
    return false
  })
}

export const distinctById = <TItem extends WithId>(collection: TItem[]) =>
  distinct(collection, (x) => x.id)

type SortOrder = 'DESC' | 'ASC'
type SortingParam = string | number

/**
 *
 * @param extractorFn function that extracts the property to be compared in the ordering
 * @returns a function/closure on "order" that returns a sorter function
 * according to Array.prototype.sort() specs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
 */

export const alphaNumericSorter =
  <TElement>(extractorFn: (el: TElement) => SortingParam) =>
  (order: SortOrder) =>
  (a: TElement, b: TElement) => {
    const first = extractorFn(a)
    const second = extractorFn(b)
    if (order === 'DESC') {
      if (first > second) {
        return -1
      }
      if (second > first) {
        return 1
      }
      return 0
    } else {
      if (first > second) {
        return 1
      }
      if (second > first) {
        return -1
      }
      return 0
    }
  }

export const hasSections = (i: { sections: unknown[] }) => !!i.sections.length

export const isActive = <T extends { status: ActivityStatus }>(e: T) =>
  e.status === 'active'

export const sortAscByMaxParticipants = <T extends { maxParticipants: number }>(
  a: T,
  b: T
) => a.maxParticipants - b.maxParticipants

/**
 * Compare 2 Sets and return true if they're equal
 */
export const areSetsEqual = <T>(a: Set<T>, b: Set<T>) =>
  a.size === b.size && Array.from(a).every((value) => b.has(value))

const TIME_LABEL_REGEX = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/
export const isTimeLabelValid = (tm?: TimeLabel) =>
  tm && TIME_LABEL_REGEX.test(tm)

/**
 * Checks the uniqueness of the specified field inside the elements of an array
 * @param field the field to validate
 */
export const isEveryNameOfKeyUnique = <
  TObject extends object,
  SKey extends keyof TObject
>(
  objects: TObject[] | undefined,
  key: SKey
) => {
  const unique = new Set(objects?.map((e) => e[key]))
  return unique.size === objects?.length
}
